/**
 * penroseStairsPipeline — the impossible Penrose staircase.
 *
 * The Penrose stairs are a classic impossible object: a staircase
 * that appears to ascend forever in a closed loop, so a person
 * walking up the stairs would never get higher. We render this
 * as a 2D projection of a "4-sided" pyramid-like structure
 * (technically the same family of impossible figures as the
 * Penrose triangle).
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PenroseStairsPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = vec3(0.04, 0.02, 0.06);
  vec3 col = bg;

  // 4 stairs arranged in a 2x2 grid.
  // Stair heights ascend: 0, 0.15, 0.30, 0.45.
  // 2.5D projection: depth shifts toward top-left.

  vec3 stairs[4];
  stairs[0] = vec3(-0.30, 0.30, 0.00);   // x, y, top_y (front-right low)
  stairs[1] = vec3(0.00, 0.30, 0.15);    // back-right
  stairs[2] = vec3(0.00, -0.30, 0.30);   // back-left
  stairs[3] = vec3(-0.30, -0.30, 0.45);  // front-left high

  vec3 stoneTop = vec3(0.85, 0.75, 0.55);
  vec3 stoneFront = vec3(0.45, 0.38, 0.25);
  vec3 stoneSide = vec3(0.30, 0.25, 0.18);
  vec3 stoneEdge = vec3(0.95, 0.85, 0.65);

  float stairH = 0.12;
  float stairW = 0.30;
  // Depth shift for 3D effect (back is shifted up-left).
  vec2 depthShift = vec2(-0.10, -0.10);

  // Render each stair with TOP, FRONT, RIGHT, BACK faces.
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    vec3 sd = stairs[i];
    float xL = sd.x;
    float yCenter = sd.y;
    float yTop = sd.z;
    float yBot = yTop - stairH;
    // Front face: rectangle from (xL, yCenter-stairW/2) to (xL+stairW, yCenter+stairW/2).
    float fxL = xL;
    float fxR = xL + stairW;
    float fyT = yCenter + stairW * 0.5;
    float fyB = yCenter - stairW * 0.5;
    // Bottom of the stair is at yBot (world), which projects to screen y.
    // For the 2.5D view, the stair is a rectangle in (x, y) world coords.
    // The y-axis here is the "depth into the screen" of the floor plan.
    // The TOP of the stair is at world y = yTop.
    // The BOTTOM of the stair is at world y = yTop - stairH.
    // Top face: a parallelogram from (xL, fyT) at height yTop
    //   to (xL + depthShift.x, fyT + depthShift.y) at the same height.
    // The top is a 3D rectangle: (xL, fyT, yTop), (fxR, fyT, yTop),
    //   (fxR, fyB, yTop), (xL, fyB, yTop), with depth shift.

    // For the front face: it's at the "near" side of the box (fyT is "near", fyB is "far").
    // The front face: (xL, fyT, yBot) to (fxR, fyT, yBot) to (fxR, fyT, yTop) to (xL, fyT, yTop).
    // Project: the front face is a 2D rectangle at x in [xL, fxR], y_screen in [-yTop, -yBot].
    // Add depth shift: front face is "near" so it's drawn WITHOUT depth shift.
    // (Or with a smaller depth shift.)

    // 2.5D: front face is at depth = fyT (small depth = near), back face at depth = fyB (large depth = far).
    // Each face's depth = average of its 4 corners' depth values.

    // Top face (a parallelogram at height yTop).
    vec2 a = vec2(xL + depthShift.x * 0.0, -yTop);
    vec2 b = vec2(fxR + depthShift.x * 0.0, -yTop);
    vec2 c = vec2(fxR + depthShift.x * 1.0, -yTop + depthShift.y * 1.0);
    vec2 d = vec2(xL + depthShift.x * 1.0, -yTop + depthShift.y * 1.0);

    // Inside check: barycentric or bounding.
    // For a parallelogram with corners a, b, d, c (counterclockwise):
    // v0 = c - a; v1 = b - a; v2 = p - a;
    // dot00 = dot(v0, v0); dot01 = dot(v0, v1); dot02 = dot(v0, v2);
    // dot11 = dot(v1, v1); dot12 = dot(v1, v2);
    // invDenom = 1.0 / (dot00 * dot11 - dot01 * dot01);
    // u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    // v = (dot00 * dot12 - dot01 * dot02) * invDenom;
    // inside if u in [0,1] AND v in [0,1].

    vec2 v0 = c - a;
    vec2 v1 = b - a;
    vec2 v2 = p - a;
    float dot00 = dot(v0, v0);
    float dot01 = dot(v0, v1);
    float dot02 = dot(v0, v2);
    float dot11 = dot(v1, v1);
    float dot12 = dot(v1, v2);
    float invDenom = 1.0 / max(dot00 * dot11 - dot01 * dot01, 0.0001);
    float u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    float v = (dot00 * dot12 - dot01 * dot02) * invDenom;
    if (u >= 0.0 && u <= 1.0 && v >= 0.0 && v <= 1.0) {
      col = mix(col, stoneTop, 0.95);
      // Edge highlight.
      float edge = min(min(u, 1.0 - u), min(v, 1.0 - v));
      if (edge < 0.05) col = mix(col, stoneEdge, 0.6);
    }

    // Front face (the face at the "near" side, y = fyT).
    // Rectangle: (xL, -yTop) to (fxR, -yBot) at depth shift = (0,0).
    if (p.x >= xL && p.x <= fxR && p.y >= -yTop && p.y <= -yBot) {
      col = mix(col, stoneFront, 0.95);
      // Edge highlight.
      float yFrac = (p.y - (-yTop)) / (-yBot - (-yTop));
      float edge = min(min(p.x - xL, fxR - p.x), min(yFrac, 1.0 - yFrac));
      if (edge < 0.01) col = mix(col, stoneEdge, 0.6);
    }

    // Right face (the face at the "right" side, x = fxR).
    // Parallelogram: (fxR, -yTop), (fxR + depthShift.x, -yTop + depthShift.y), (fxR + depthShift.x, -yBot + depthShift.y), (fxR, -yBot).
    vec2 rA = vec2(fxR, -yTop);
    vec2 rB = vec2(fxR + depthShift.x, -yTop + depthShift.y);
    vec2 rC = vec2(fxR + depthShift.x, -yBot + depthShift.y);
    vec2 rD = vec2(fxR, -yBot);
    v0 = rB - rA;
    v1 = rD - rA;
    v2 = p - rA;
    dot00 = dot(v0, v0);
    dot01 = dot(v0, v1);
    dot02 = dot(v0, v2);
    dot11 = dot(v1, v1);
    dot12 = dot(v1, v2);
    invDenom = 1.0 / max(dot00 * dot11 - dot01 * dot01, 0.0001);
    u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    v = (dot00 * dot12 - dot01 * dot02) * invDenom;
    if (u >= 0.0 && u <= 1.0 && v >= 0.0 && v <= 1.0) {
      col = mix(col, stoneSide, 0.95);
      float edge = min(min(u, 1.0 - u), min(v, 1.0 - v));
      if (edge < 0.05) col = mix(col, stoneEdge, 0.6);
    }
  }

  // A small figure walking on the stairs.
  // The figure walks in a circle around the staircase.
  float walkT = mod(uTime * 0.4, 6.2831);
  vec2 figPos;
  float walkSeg = 1.5708;
  if (walkT < walkSeg) {
    vec2 a = vec2(stairs[0].x, -stairs[0].z);
    vec2 b = vec2(stairs[1].x, -stairs[1].z);
    figPos = mix(a, b, walkT / walkSeg) + vec2(0.0, -0.05);
  } else if (walkT < 2.0 * walkSeg) {
    vec2 a = vec2(stairs[1].x, -stairs[1].z);
    vec2 b = vec2(stairs[2].x, -stairs[2].z);
    figPos = mix(a, b, (walkT - walkSeg) / walkSeg) + vec2(0.0, -0.05);
  } else if (walkT < 3.0 * walkSeg) {
    vec2 a = vec2(stairs[2].x, -stairs[2].z);
    vec2 b = vec2(stairs[3].x, -stairs[3].z);
    figPos = mix(a, b, (walkT - 2.0 * walkSeg) / walkSeg) + vec2(0.0, -0.05);
  } else {
    vec2 a = vec2(stairs[3].x, -stairs[3].z);
    vec2 b = vec2(stairs[0].x, -stairs[0].z);
    figPos = mix(a, b, (walkT - 3.0 * walkSeg) / walkSeg) + vec2(0.0, -0.05);
  }
  float figD = length(p - figPos);
  if (figD < 0.025) {
    col = mix(col, vec3(1.0, 0.4, 0.3), 0.9);
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computePenroseStairsBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computePenroseStairsRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPenroseStairsPipeline(gl: WebGL2RenderingContext): PenroseStairsPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPenroseStairs(
  gl: WebGL2RenderingContext,
  pipe: PenroseStairsPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
